import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import _get from 'lodash/get';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { cardThemes } from '../ProductCard/ProductCard.config';
import { getPriceFormatWithCommas } from '../../helpers/Pricing/Pricing';
import * as styles from './Pricing.css';
import BogoTag from '../../assets/svg/BogoTag.svg';

const cx = classNames.bind(styles);
const priceDisplayPattern = /^([\d,]+)\.?(\d{0,2})/;
const newPriceFormat = (priceValue) => {
    if (!priceValue) {
        return priceValue;
    }
    const results = [];
    const data = cardThemes.CARTRIDGE ? `${getPriceFormatWithCommas(priceValue)}` : `${priceValue}`;
    const value = priceDisplayPattern.exec(data.trim());
    if (!value) {
        results.push(data);
        return results;
    }
    const [dollarAndCent] = value;

    results.push(`$${dollarAndCent}`);

    return results;
};

const stripZerosPattern = /(\.00$|\.$)/;

class Pricing extends Component {
    static defaultProps = {
        pricingDetails: {},
        dynamicFont: false,
        priceWrapperClass: '',
        priceClass: '',
        mainPriceClass: '',
        priceLabelClass: '',
        priceSummaryClass: '',
        customMarketingLabel: '',
        mapPriceClass: '',
        pricingConfig: undefined,
        deviceType: {},
        ppId: '',
        displayEach: false,
        priceLabel: {},
        disableEachLabel: false,
        cardTheme: '',
        enableCustomAfterCouponLabel: false,
        eligibilityMessage: '',
        enableMarketingLabel: false,
    };

    static propTypes = {
        pricingDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
        dynamicFont: PropTypes.bool,
        priceWrapperClass: PropTypes.string,
        priceClass: PropTypes.string,
        mainPriceClass: PropTypes.string,
        priceLabelClass: PropTypes.string,
        priceSummaryClass: PropTypes.string,
        customMarketingLabel: PropTypes.string,
        mapPriceClass: PropTypes.string,
        pricingConfig: PropTypes.object,
        deviceType: PropTypes.object,
        ppId: PropTypes.string,
        displayEach: PropTypes.bool,
        priceLabel: PropTypes.object,
        disableEachLabel: PropTypes.bool,
        cardTheme: PropTypes.string,
        enableCustomAfterCouponLabel: PropTypes.bool,
        eligibilityMessage: PropTypes.string,
        enableMarketingLabel: PropTypes.bool,
    };

    getNewCouponPriceDetails(showNewPriceFormat, displayPrice, rangePrice, deviceType) {
        if (!showNewPriceFormat) {
            return {};
        }
        const priceFormat = newPriceFormat(displayPrice);
        const price = isNaN(displayPrice)
            ? ''
            : String(displayPrice).replace(stripZerosPattern, '');
        const isDecimalExist = price.indexOf('.') !== -1;
        const priceLength = price.length;
        let isWrapText;
        if (rangePrice) {
            isWrapText = priceLength <= 2;
        } else {
            isWrapText = isDecimalExist ? priceLength <= 7 : priceLength <= 5;
            // In Mobile channel, due to space constrainst going to second with wrap. So fix
            // is made to move to next line without wrap
            if (deviceType.isMobile) {
                isWrapText = isDecimalExist ? priceLength <= 6 : priceLength <= 5;
            }
        }
        const isFullText = !isWrapText;
        return {
            newPrice: priceFormat,
            isWrapText,
            isFullText,
        };
    }

    getFPACDisplayDetail() {
        const {
            from,
            afterCoupon,
            disableFPACAPI,
            disableFPAC,
            disableFromLableInRange,
            enablePriceSuperScript,
        } = this.props.pricingConfig || {};
        const { enableCustomAfterCouponLabel, eligibilityMessage } = this.props;
        const showNewPriceFormat = disableFPACAPI === false;
        const showCouponInfoPrice = showNewPriceFormat && disableFPAC === false;
        const fromLabel = from;
        const afterCouponLabel =
            enableCustomAfterCouponLabel && eligibilityMessage ? eligibilityMessage : afterCoupon;
        return {
            showNewPriceFormat,
            showCouponInfoPrice,
            disableFromLableInRange,
            enablePriceSuperScript,
            ...(showNewPriceFormat ? { fromLabel, afterCouponLabel } : undefined),
            ...(enableCustomAfterCouponLabel ? { afterCouponLabel } : undefined),
        };
    }

    getFPACRenderDetail(pricingDetails) {
        const { fpac, nonfpac } = this.props.pricingConfig || {};
        let { disableWasPrice = false, disablePercentageOff = false } = nonfpac || {};

        if (pricingDetails.type === 'fpac' || pricingDetails.type === 'fpac-range') {
            disableWasPrice = (fpac && fpac.disableWasPrice) || false;
            disablePercentageOff = (fpac && fpac.disablePercentageOff) || false;
        }
        const showWasSummary = !disableWasPrice;
        const showPercentOff = !disablePercentageOff;
        return {
            showWasSummary,
            showPercentOff,
        };
    }

    getPriceRangeInSuperScript(displayPrice) {
        const numberMatchingPattern = /[^0-9.]+/g;
        const priceArray = `${displayPrice}`.split('-');
        return priceArray.map((price, index) => {
            let priceVal = Number(price.replace(numberMatchingPattern, ''));
            priceVal = priceVal % 1 !== 0 ? priceVal.toFixed(2) : priceVal;
            const priceInSubScriptFormat = newPriceFormat(priceVal);

            if (index !== priceArray.length - 1) {
                priceInSubScriptFormat.push(' - ');
            }
            return priceInSubScriptFormat;
        });
    }

    getNewRangePrice(displayPrice, isPriceRange) {
        const { pricingConfig: { enablePriceSuperScript = false } = {} } = this.props;
        if (isPriceRange && enablePriceSuperScript) {
            return this.getPriceRangeInSuperScript(displayPrice);
        }
        return newPriceFormat(displayPrice);
    }

    getDisplayPrice() {
        const {
            deviceType,
            disableEachLabel,
            displayEach,
            dynamicFont,
            eligibilityMessage,
            enableCustomAfterCouponLabel,
            mainPriceClass,
            mapPriceClass,
            ppId,
            priceClass,
            priceLabel,
            priceLabelClass,
            priceWrapperClass,
            pricingDetails,
        } = this.props;

        const {
            afterCouponLabel,
            disableFromLableInRange,
            enablePriceSuperScript,
            fromLabel,
            showCouponInfoPrice,
            showNewPriceFormat,
        } = this.getFPACDisplayDetail();

        const {
            brand,
            defaults,
            defaultsPriceValue,
            eachLabel,
            fpac,
            fpacCoupon,
            fpacPrice,
            isDefaultsPriceRange,
            isOriginalPriceRange,
            mapPrice,
            original,
            originalPriceValue,
            showNoPriceLabel,
            subDivision,
            type,
        } = pricingDetails;

        const localPrice = defaults || original;
        const isPriceRange = isDefaultsPriceRange || isOriginalPriceRange;
        const displayPrice = isPriceRange ? localPrice : defaultsPriceValue || originalPriceValue;

        const newLocalPrice = enablePriceSuperScript && (
            <span className={cx('priceInBlock')}>
                {this.getNewRangePrice(displayPrice, isPriceRange)}
            </span>
        );

        const priceCustomClass = cx(
            'priceWrapper',
            { supPriceFormat: showNewPriceFormat },
            priceWrapperClass
        );

        const { showWasSummary, showPercentOff } = this.getFPACRenderDetail(pricingDetails);
        const showSaleSummary = showWasSummary || showPercentOff;
        const isFpacRange = type === 'fpac-range';

        if (type === 'fpac' || isFpacRange) {
            const fpacPriceRange = disableFromLableInRange ? fpac : fpacPrice;

            const { isFullText, isWrapText, newPrice } = this.getNewCouponPriceDetails(
                showCouponInfoPrice,
                fpacPriceRange,
                isFpacRange,
                deviceType
            );

            const fpacNewLocalPrice = enablePriceSuperScript ? (
                <span className={cx('priceInBlock')}>
                    {this.getNewRangePrice(newPrice, enablePriceSuperScript)}
                </span>
            ) : (
                newPrice
            );
            const priceLimit = 7; // for mobile devices to check for length of the price
            const fpacCouponTextMobile =
                ((fpacNewLocalPrice && fpacNewLocalPrice.length) || (fpac && fpac.length)) >
                    priceLimit && 'fpacCouponTextLength';

            return (
                <div className={cx('fpacBlock', { newFPACWrapper: showCouponInfoPrice })}>
                    {isFpacRange && !disableFromLableInRange && (
                        <span className={cx('fpacText')}>{fromLabel || 'from'}</span>
                    )}
                    <span
                        className={cx('fpacPrice', {
                            fpacNoRange: !isFpacRange,
                            noSpaceText:
                                isFullText || (enableCustomAfterCouponLabel && eligibilityMessage),
                            wrap: isWrapText,
                        })}
                    >
                        {fpacNewLocalPrice || fpac}
                    </span>
                    <span
                        className={cx('fpacText', fpacCouponTextMobile, {
                            inlinefpacText: !dynamicFont,
                            fpacRange: isFpacRange,
                            newFPACCouponFullText:
                                isFullText || (enableCustomAfterCouponLabel && eligibilityMessage),
                            newFPACCouponText: isWrapText,
                            customAfterCouponLabel:
                                enableCustomAfterCouponLabel && eligibilityMessage,
                        })}
                    >
                        {afterCouponLabel || 'with code'}
                    </span>
                    {this.renderWasPriceStrike(showSaleSummary, showWasSummary, showPercentOff)}
                    <input type="hidden" value={fpacCoupon} className="fpacCoupon" />
                    {ppId && <input type="hidden" value={ppId} className="ppId" />}
                    {brand && <input type="hidden" value={brand} className="brand" />}
                    {subDivision && (
                        <input className="subDivision" type="hidden" value={subDivision} />
                    )}
                    {this.getPriceDetails(pricingDetails)}
                </div>
            );
        }
        const mainPriceClazz = showNewPriceFormat
            ? cx('price', priceClass, mainPriceClass)
            : cx('price', priceClass);

        let pricingLabel = '';

        if (showNoPriceLabel) {
            pricingLabel = priceLabel.noPriceLabel;
        } else if (mapPrice) {
            pricingLabel = priceLabel.mapPrice;
        }

        const showEach = !disableEachLabel && eachLabel && eachLabel === 'each';

        return (
            <>
                {pricingLabel ? (
                    <p
                        className={cx('mapPrice', mapPriceClass)}
                        data-automation-id="at-price-map-price-label"
                    >
                        <span className={cx('pricingLabel')}>{pricingLabel}</span>
                        {this.getPriceDetails(pricingDetails)}
                        <div
                            className={dt([
                                'font-open-sans',
                                'font-normal',
                                'italic',
                                'text-sm',
                                'leading-5',
                                'tracking-normal',
                                'line-through',
                                'text-gray-60',
                                'inline',
                                'w-full',
                            ])}
                        >
                            {original}
                        </div>
                    </p>
                ) : (
                    <p
                        data-automation-id={`${type}-price-info`}
                        ref={(priceText) => {
                            this.priceText = priceText;
                        }}
                        className={priceCustomClass}
                    >
                        {this.getPriceDetails(pricingDetails)}
                        {showNewPriceFormat && isPriceRange && !disableFromLableInRange && (
                            <span className={cx('newPriceText')}>{fromLabel || 'from'}</span>
                        )}
                        <span data-automation-id="at-price-value" className={mainPriceClazz}>
                            {newLocalPrice || localPrice}
                            {showEach ? (
                                <span className={cx('eachLabelBundleInfo')}>each</span>
                            ) : null}
                        </span>
                        {displayEach && <span className={cx('eachLabel')}>each</span>}
                        {type && (
                            <span
                                data-automation-id="at-price-label"
                                className={cx(priceLabelClass, {
                                    sale: true,
                                    clearence: type === 'clearance' || false,
                                })}
                            >
                                {type}
                            </span>
                        )}
                        {this.renderWasPriceStrike(showSaleSummary, showWasSummary, showPercentOff)}
                    </p>
                )}
            </>
        );
    }

    getPriceDetails(pricingDetails) {
        return (
            <>
                {pricingDetails.currentMin ? (
                    <input type="hidden" value={pricingDetails.currentMin} className="currentMin" />
                ) : null}
                {pricingDetails.currentMax ? (
                    <input type="hidden" value={pricingDetails.currentMax} className="currentMax" />
                ) : null}
                {pricingDetails.originalPriceLabel ? (
                    <input
                        type="hidden"
                        value={pricingDetails.originalPriceLabel}
                        className="originalPriceLabel"
                    />
                ) : null}
                {pricingDetails.currentPriceLabel ? (
                    <input
                        type="hidden"
                        value={pricingDetails.currentPriceLabel}
                        className="currentPriceLabel"
                    />
                ) : null}
            </>
        );
    }

    isSaleSummary(pricingDetails) {
        return (
            pricingDetails.originalType ||
            pricingDetails.original ||
            pricingDetails.dashed ||
            pricingDetails.percentOff
        );
    }

    // renderDesktopWasPrice (showSaleSummary, showWasSummary, showPercentOff) {
    //     const {
    //         pricingDetails,
    //         priceSummaryClass,
    //     } = this.props;
    //     const {
    //         originalType,
    //         percentOff,
    //         original,
    //         dashed,
    //     } = pricingDetails || {};

    //     return (showSaleSummary && this.isSaleSummary(pricingDetails) ? <div data-automation-id="price-old-sale" className={cx('salesummary', priceSummaryClass)}>
    //         {showWasSummary && originalType} { showWasSummary && original} {showWasSummary && showPercentOff ? dashed : ''}
    //         {showPercentOff && percentOff ? <span data-automation-id="price-percent-off" className={cx('percentageOff')}>{percentOff}</span> : ''}
    //     </div> : ''
    //     );
    // }

    renderWasPriceStrike(showSaleSummary, showWasSummary, showPercentOff) {
        const { pricingDetails, priceSummaryClass } = this.props;
        const { percentOff, original, dashed } = pricingDetails || {};

        return (
            showSaleSummary &&
            this.isSaleSummary(pricingDetails) && (
                <div
                    data-automation-id="price-old-sale"
                    className={cx('salesummary', priceSummaryClass)}
                >
                    {showWasSummary && original && <strike>{showWasSummary && original}</strike>}
                    {showWasSummary && showPercentOff ? dashed : ''}
                    {showPercentOff && percentOff && (
                        <span
                            data-automation-id="price-percent-off"
                            className={cx('percentageOff')}
                        >
                            {percentOff}
                        </span>
                    )}
                </div>
            )
        );
    }

    render() {
        const {
            pricingDetails,
            customMarketingLabel,
            cardTheme,
            enableMarketingLabel,
        } = this.props;

        const { promotions, marketingLabel } = pricingDetails || {};

        if (!pricingDetails) {
            return null;
        }

        const isGallaryTheme = cardTheme === cardThemes.GALLERY;

        return (
            <div className={cx('pricing', 'displayPriceWrapper', { gallery: isGallaryTheme })}>
                {(promotions || marketingLabel) && (
                    <div className={cx('customerMarketingLabelWrap')}>
                        {promotions ? (
                            <p
                                className={cx('marketingLabel', customMarketingLabel)}
                                data-automation-id="at-price-promotions-label"
                            >
                                {promotions.toString().match('BUY 1 GET 1') ? (
                                    <Icon
                                        iconType="svg"
                                        width="24px"
                                        height="24px"
                                        viewBox="0 0 24 24"
                                    >
                                        <BogoTag />
                                    </Icon>
                                ) : (
                                    ''
                                )}
                                {promotions}
                            </p>
                        ) : (
                            ''
                        )}
                        {marketingLabel && enableMarketingLabel ? (
                            <p
                                className={cx('marketingLabel', customMarketingLabel)}
                                data-automation-id="at-price-marketing-label"
                            >
                                {marketingLabel}
                            </p>
                        ) : (
                            ''
                        )}
                    </div>
                )}
                {this.getDisplayPrice()}
            </div>
        );
    }
}
const defaultPriceLabel = { noPriceLabel: 'View Price in Cart', mapPrice: 'View Price in Cart' };

const mapStateToProps = ({ context }) => ({
    priceLabel: _get(context, 'preferences.priceLabel', defaultPriceLabel),
    disableEachLabel: _get(context, 'featureFlags.disableEachLabel', false), // Common flag for certona and hotdeals
    enableCustomAfterCouponLabel: _get(context, 'featureFlags.enableCustomAfterCouponLabel', false),
    enableMarketingLabel: _get(context, 'featureFlags.enableMarketingLabel', false),
});

export default connect(mapStateToProps)(Pricing);
